import { Claim } from "@/types";
import { cn } from "@/lib/utils";
import { 
  CheckCircle2, 
  FileText, 
  Mail, 
  AlertTriangle,
  FileCheck, 
  Calculator, 
  Ruler, 
  CheckCircle, 
  Ban,
  PencilRuler
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import React, { useState, useEffect } from "react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  ClaimStatus, 
  CLAIM_STATUS_SEQUENCE,
  getStatusDisplayName,
  getStatusIndex,
  isTerminalStatus
} from "@/lib/claim-status-constants";

// Navigation context types (matching claim-detail-page.tsx)
type ViewMode = 'active' | 'reviewing';

interface NavigationContext {
  viewMode: ViewMode;
  reviewingStage: ClaimStatus | null;
  activeStage: ClaimStatus;
}

interface ClaimTimelineProps {
  claim: Omit<Claim, 'status'> & { status: ClaimStatus };
  navigationContext: NavigationContext;
  onStageNavigation: (stage: ClaimStatus) => void;
  onOpenEmailDialog: () => void;
}

// Input type for adding a communication log
interface AddLogInput {
  claimId: string;
  logType: 'Note' | 'Email Sent' | 'Email Received' | 'Call'; // Match CommunicationLog type
  subject?: string;
  body: string;
}

type ClaimStage = {
  id: string;
  icon: React.ReactNode;
  name: string;
  description: string;
  status: "complete" | "current" | "pending";
  actionRequired: "owner" | "contractor" | null;
  actionLabel?: string;
};

export default function ClaimTimeline({ claim, navigationContext, onStageNavigation, onOpenEmailDialog }: ClaimTimelineProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [selectedStage] = useState<string | null>(null);
  const [isWideScreen, setIsWideScreen] = useState(false);
  
  // Check screen width for responsive layout
  useEffect(() => {
    const checkScreenWidth = () => {
      setIsWideScreen(window.innerWidth >= 1024); // lg breakpoint
    };
    
    // Check initially
    checkScreenWidth();
    
    // Add resize listener
    window.addEventListener('resize', checkScreenWidth);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenWidth);
  }, []);

  // Update claim status mutation
  const updateClaimMutation = useMutation({
    mutationFn: async (status: ClaimStatus) => {
      const res = await apiRequest("PUT", `/api/claims/${claim.claimId}`, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      toast({
        title: "Status updated",
        description: "Claim status has been updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update claim status",
        variant: "destructive",
      });
    },
  });

  // Add communication log mutation
  const addLogMutation = useMutation({
    mutationFn: async (logData: AddLogInput) => {
      const res = await apiRequest(
        "POST", 
        `/api/claims/${claim.claimId}/communicationLogs`, 
        logData
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      toast({
        title: "Log added",
        description: "Communication log has been added successfully",
      });
      setNoteText("");
      setIsOpen(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add communication log",
        variant: "destructive",
      });
    },
  });

  // Handle next stage transition
  const handleChangeStage = (newStatus: ClaimStatus) => {
    updateClaimMutation.mutate(newStatus);
  };

  // Handle adding a note
  const handleAddNote = () => {
    if (!noteText.trim()) return;
    
    addLogMutation.mutate({
      claimId: claim.claimId,
      logType: "Note",
      subject: `Note regarding ${selectedStage}`,
      body: noteText,
    });
  };

  // Find the index of the current stage using shared constants - unused but kept for future reference
  const _currentStageIndex = getStatusIndex(claim.status);
  
  // Function to determine stage status (complete/current/pending) with navigation context
  const getStageStatus = (stageId: string): "complete" | "current" | "pending" => {
    const actualClaimStatus = claim.status;
    const stageIndex = CLAIM_STATUS_SEQUENCE.indexOf(stageId as ClaimStatus);
    const actualCurrentIndex = getStatusIndex(actualClaimStatus);
    
    // Determine base status based on actual claim progress
    let baseStatus: "complete" | "current" | "pending";
    
    if (isTerminalStatus(actualClaimStatus)) {
      if (stageId === actualClaimStatus) {
        baseStatus = "current"; // The active final state
      } else {
        baseStatus = stageIndex < actualCurrentIndex ? "complete" : "pending";
      }
    } else {
      if (stageIndex < actualCurrentIndex) baseStatus = "complete";
      else if (stageIndex === actualCurrentIndex) baseStatus = "current";
      else baseStatus = "pending";
    }
    
    // Override for navigation context - if we're reviewing this stage, make it current
    if (navigationContext.viewMode === 'reviewing' && stageId === navigationContext.reviewingStage) {
      return "current";
    }
    
    return baseStatus;
  };
  
  // Helper function to determine if a stage is clickable for navigation
  const isStageClickable = (stageId: string): boolean => {
    const actualClaimStatus = claim.status;
    const stageIndex = CLAIM_STATUS_SEQUENCE.indexOf(stageId as ClaimStatus);
    const actualCurrentIndex = getStatusIndex(actualClaimStatus);
    
    // Can click on completed stages or current stage
    return stageIndex <= actualCurrentIndex;
  };

  // Handle stage click for navigation
  const handleStageClick = (stageId: string) => {
    if (isStageClickable(stageId)) {
      onStageNavigation(stageId as ClaimStatus);
    }
  };

  // Helper function to determine if a stage requires action
  const getActionRequired = (stageId: string): "owner" | "contractor" | null => {
    // Only show action required for the actual active stage, not when reviewing
    if (navigationContext.viewMode === 'reviewing') return null;
    
    // For stages, check if current stage matches this stage ID
    if (claim.status !== stageId) return null;
    
    // Only the current stage can have an action required
    if (getStageStatus(stageId) !== "current") return null;
    
    // For now, all actions are owner actions
    return "owner";
  };

  // Helper function to get stage metadata
  const getStageMetadata = (status: ClaimStatus) => {
    switch (status) {
      case "Acknowledgment":
        return {
          icon: <Mail className="h-6 w-6" />,
          description: "Confirm receipt to contractor",
          actionLabel: "Send Acknowledgment Email"
        };
      case "Documents":
        return {
          icon: <FileCheck className="h-6 w-6" />,
          description: "Upload all claim documents", 
          actionLabel: "Submit Documents for Detail Extraction"
        };
      case "Details":
        return {
          icon: <PencilRuler className="h-6 w-6" />,
          description: "Extract and confirm claim details",
          actionLabel: "Confirm Claim Details"
        };
      case "Contract Review":
        return {
          icon: <FileText className="h-6 w-6" />,
          description: "Assess claim validity against contract",
          actionLabel: "Complete Review"
        };
      case "Quantity Review":
        return {
          icon: <Ruler className="h-6 w-6" />,
          description: "Assess time & materials validity",
          actionLabel: "Complete Review"
        };
      case "Calculation Review":
        return {
          icon: <Calculator className="h-6 w-6" />,
          description: "Assess calculations validity",
          actionLabel: "Complete Review"
        };
      case "Final Decision":
        return {
          icon: <AlertTriangle className="h-6 w-6" />,
          description: "Make final decision on claim",
          actionLabel: "Make Decision"
        };
      case "Approved":
        return {
          icon: <CheckCircle className="h-6 w-6" />,
          description: "Claim has been approved",
          actionLabel: ""
        };
      case "Rejected":
        return {
          icon: <Ban className="h-6 w-6" />,
          description: "Claim has been rejected", 
          actionLabel: ""
        };
      case "Withdrawn":
        return {
          icon: <Ban className="h-6 w-6" />,
          description: "Claim has been withdrawn",
          actionLabel: ""
        };
      default:
        return {
          icon: <FileText className="h-6 w-6" />,
          description: "Stage description",
          actionLabel: "Complete Stage"
        };
    }
  };

  // Helper function to determine which terminal stage to show (if any)
  const getEffectiveTerminalStage = (): ClaimStatus | null => {
    if (isTerminalStatus(claim.status)) {
      return claim.status;
    }
    return null;
  };

  // Generate stages dynamically from CLAIM_STATUS_SEQUENCE
  const stages: ClaimStage[] = CLAIM_STATUS_SEQUENCE
    .filter((status) => {
      // Filter out terminal statuses from the sequence - we'll handle them separately
      return !isTerminalStatus(status);
    })
    .map((status) => {
      const metadata = getStageMetadata(status);
      
      return {
        id: status,
        icon: metadata.icon,
        name: getStatusDisplayName(status),
        description: metadata.description,
        status: getStageStatus(status),
        actionRequired: getActionRequired(status),
        actionLabel: metadata.actionLabel,
      };
    });

  // Add the appropriate terminal stage if the claim is in a terminal status
  const effectiveTerminalStage = getEffectiveTerminalStage();
  if (effectiveTerminalStage) {
    const terminalMetadata = getStageMetadata(effectiveTerminalStage);
    stages.push({
      id: effectiveTerminalStage,
      icon: terminalMetadata.icon,
      name: getStatusDisplayName(effectiveTerminalStage),
      description: terminalMetadata.description,
      status: getStageStatus(effectiveTerminalStage),
      actionRequired: getActionRequired(effectiveTerminalStage),
      actionLabel: terminalMetadata.actionLabel,
    });
  }

  // Get current stage
  // const currentStage = stages.find(stage => stage.status === "current");
  const currentIndex = stages.findIndex(stage => stage.status === "current");
  // The nextStage could be any stage ID, which we'll need to cast when using it
  const nextStage: string | null = currentIndex >= 0 && currentIndex < stages.length - 1 ? 
                  stages[currentIndex + 1].id : null;

  return (
    <div className="space-y-6">
      {isWideScreen ? (
        // Horizontal timeline for wider screens
        <div className="relative mt-8 pb-4">
          {/* Horizontal line */}
          <div className="absolute left-0 right-0 top-6 h-0.5 bg-neutral-200"></div>
          
          {/* Stage items */}
          <div className="flex justify-between items-start">
            {stages.map((stage) => {
              const clickable = isStageClickable(stage.id);
              return (
                <div 
                  key={stage.id} 
                  className={cn(
                    "relative px-2 flex-1 text-center",
                    clickable ? "cursor-pointer" : "cursor-default"
                  )}
                  onClick={() => handleStageClick(stage.id)}
                >
                  {/* Timeline node */}
                  <div 
                    className={cn(
                      "mx-auto h-12 w-12 rounded-full border-2 flex items-center justify-center z-10 transition-all duration-200",
                      stage.status === "complete" ? "bg-primary border-primary text-white" :
                      stage.status === "current" ? "bg-white border-primary text-primary ring-2 ring-primary/20" :
                      "bg-white border-neutral-300 text-neutral-300",
                      clickable && stage.status === "complete" ? "hover:bg-primary/90 hover:scale-105" : "",
                      clickable && stage.status === "current" ? "hover:ring-primary/30" : ""
                    )}
                  >
                    {stage.status === "complete" ? 
                      <CheckCircle2 className="h-6 w-6" /> : 
                      stage.icon}
                  </div>
                
                {/* Content */}
                <div className="mt-2">
                  <h3 className={cn(
                    "text-sm font-medium",
                    stage.status === "pending" ? "text-neutral-500" : "text-neutral-900"
                  )}>
                    {stage.name}
                  </h3>
                  
                  <p className={cn(
                    "text-xs mt-1",
                    stage.status === "pending" ? "text-neutral-400" : "text-neutral-600"
                  )}>
                    {stage.description}
                  </p>
                  
                  {stage.actionRequired && (
                    <Badge 
                      className={cn(
                        "mt-1", 
                        stage.actionRequired === "owner" ? "bg-primary" : "bg-orange-500"
                      )}
                    >
                      {stage.actionRequired === "owner" ? "Your Action" : "Contractor"}
                    </Badge>
                  )}
                </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // Vertical timeline for narrower screens
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-neutral-200"></div>
          
          {/* Stage items */}
          <div className="space-y-8">
            {stages.map((stage) => {
              const clickable = isStageClickable(stage.id);
              return (
                <div 
                  key={stage.id} 
                  className={cn(
                    "relative",
                    clickable ? "cursor-pointer" : "cursor-default"
                  )}
                  onClick={() => handleStageClick(stage.id)}
                >
                  {/* Timeline node */}
                  <div 
                    className={cn(
                      "absolute left-6 -ml-3 h-6 w-6 rounded-full border-2 flex items-center justify-center z-10 transition-all duration-200",
                      stage.status === "complete" ? "bg-primary border-primary text-white" :
                      stage.status === "current" ? "bg-white border-primary text-primary" :
                      "bg-white border-neutral-300 text-neutral-300",
                      clickable && stage.status === "complete" ? "hover:bg-primary/90 hover:scale-105" : "",
                      clickable && stage.status === "current" ? "hover:ring-2 hover:ring-primary/30" : ""
                    )}
                  >
                    {stage.status === "complete" && <CheckCircle2 className="h-4 w-4" />}
                  </div>
                
                {/* Content */}
                <div className="ml-12 pt-0.5">
                  <h3 className={cn(
                    "text-base font-medium flex items-center",
                    stage.status === "pending" ? "text-neutral-500" : "text-neutral-900"
                  )}>
                    {stage.icon && <span className="mr-2">{stage.icon}</span>}
                    {stage.name}
                    
                    {stage.actionRequired && (
                      <Badge 
                        className={cn(
                          "ml-2", 
                          stage.actionRequired === "owner" ? "bg-primary" : "bg-orange-500"
                        )}
                      >
                        {stage.actionRequired === "owner" ? "Your Action" : "Contractor Action"}
                      </Badge>
                    )}
                  </h3>
                  <p className={cn(
                    "text-sm",
                    stage.status === "pending" ? "text-neutral-400" : "text-neutral-600"
                  )}>
                    {stage.description}
                  </p>
                  
                  {stage.status === "current" && stage.actionRequired === "owner" && stage.actionLabel && (
                    <div className="mt-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          if (stage.id === "Acknowledgment") {
                            onOpenEmailDialog();
                          } else {
                            // Handle other actions
                            const nextStatus = nextStage ? nextStage : stage.id;
                            if (nextStatus !== stage.id) {
                              // We need to cast here because nextStage might be any string
                              // In a real app, we'd do validation to ensure it's a valid claim status
                              handleChangeStage(nextStatus as ClaimStatus);
                            }
                          }
                        }}
                      >
                        {stage.actionLabel}
                      </Button>
                    </div>
                  )}
                </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Add Note Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Add a note regarding the current stage of the claim.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note">Note</Label>
              <Textarea 
                id="note" 
                rows={5} 
                placeholder="Enter your note here..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddNote}
              disabled={!noteText.trim() || addLogMutation.isPending}
            >
              {addLogMutation.isPending ? "Adding..." : "Add Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}